// Helper functions to provide full localization for Products and ProductDetail pages
// Supporting English (en), Marathi (mr), and Kannada (kn)

export const normalizeLang = (lang) => {
  if (!lang) return 'en';
  if (lang.startsWith('mr')) return 'mr';
  if (lang.startsWith('kn')) return 'kn';
  return 'en';
};

// ─── ON-DEMAND AUTO TRANSLATION HELPER ──────────────────────────────────────
// Translates text from English to target language (mr / kn) with caching and sentence splitting
export const translateProductText = async (text, targetLang) => {
  if (!text || !targetLang || targetLang === 'en') return text;
  const lang = normalizeLang(targetLang);
  if (lang === 'en') return text;

  const trimmed = text.trim();
  if (!trimmed) return '';

  const cacheKey = `prod_trans_${lang}_${encodeURIComponent(trimmed.slice(0, 100))}_${trimmed.length}`;
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) return cached;
  } catch (e) {}

  // If short text, translate in a single request
  if (trimmed.length <= 400) {
    try {
      const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(trimmed)}&langpair=en|${lang}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data && data.responseData && data.responseData.translatedText) {
        const translated = data.responseData.translatedText;
        try { localStorage.setItem(cacheKey, translated); } catch (e) {}
        return translated;
      }
    } catch (err) {
      console.warn('Auto translation failed:', err);
    }
    return text;
  }

  // If longer text, split by sentences to respect API limits
  const sentences = trimmed.split(/(?<=[.?!।\n])\s+/);
  const translatedChunks = [];
  for (const sentence of sentences) {
    if (!sentence.trim()) continue;
    try {
      const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(sentence)}&langpair=en|${lang}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data && data.responseData && data.responseData.translatedText) {
        translatedChunks.push(data.responseData.translatedText);
      } else {
        translatedChunks.push(sentence);
      }
    } catch (e) {
      translatedChunks.push(sentence);
    }
  }

  const result = translatedChunks.join(' ');
  try { localStorage.setItem(cacheKey, result); } catch (e) {}
  return result;
};

// ─── LOCALIZED PRODUCT NAME ─────────────────────────────────────────────────
export const getLocalizedProductName = (product, lang = 'en') => {
  if (!product) return '';
  const currentLang = normalizeLang(lang);
  const name = typeof product === 'string' ? product : (product.name || '');
  if (currentLang === 'en') return name;

  // 1. Direct language field on product object (from dynamic admin addition)
  if (typeof product === 'object') {
    if (currentLang === 'mr' && (product.name_mr || product.nameMr)) {
      return product.name_mr || product.nameMr;
    }
    if (currentLang === 'kn' && (product.name_kn || product.nameKn)) {
      return product.name_kn || product.nameKn;
    }
  }

  // 2. Pre-defined dictionary for known machinery
  const lower = name.toLowerCase();

  // 1. Bedana / Raisins
  if (lower.includes('bedana') || lower.includes('raisin')) {
    if (lower.includes('washing')) {
      if (currentLang === 'mr') return 'ऑटोमॅटिक बेदाणा वॉशिंग लाईन';
      if (currentLang === 'kn') return 'ಸ್ವಯಂಚಾಲಿತ ಒಣದ್ರಾಕ್ಷಿ ವಾಷಿಂಗ್ ಲೈನ್';
      return 'Automatic Raisins Washing Line';
    }
    if (currentLang === 'mr') return 'बेदाणा मशीन';
    if (currentLang === 'kn') return 'ಒಣದ್ರಾಕ್ಷಿ ಸಂಸ್ಕರಣಾ ಯಂತ್ರ (ಬೆದಾನ ಮೆಷಿನ್)';
    return name;
  }

  // 2. Air Classifier
  if (lower.includes('classifier') || lower.includes('air classifier')) {
    if (currentLang === 'mr') return 'एअर क्लासिफायर मशीन';
    if (currentLang === 'kn') return 'ಏರ್ ಕ್ಲಾಸಿಫೈಯರ್ ಯಂತ್ರ';
    return 'Air Classifier';
  }

  // 3. Bucket Elevator
  if (lower.includes('elevator') || lower.includes('bucket elevator')) {
    if (currentLang === 'mr') return 'बकेट एलिव्हेटर';
    if (currentLang === 'kn') return 'ಬಕೆಟ್ ಎಲಿವೇಟರ್';
    return 'Bucket Elevator';
  }

  // 4. Conveyor
  if (lower.includes('conveyor') || lower.includes('loading')) {
    if (lower.includes('loading') && lower.includes('unloading')) {
      if (currentLang === 'mr') return 'लोडिंग आणि अनलोडिंग कन्व्हेयर बेल्ट';
      if (currentLang === 'kn') return 'ಲೋಡಿಂಗ್ ಮತ್ತು ಅನ್‌ಲೋಡಿಂಗ್ ಕನ್ವೇಯರ್ ಬೆಲ್ಟ್';
      return 'Loading & Unloading Conveyor Belt';
    }
    if (currentLang === 'mr') return 'कन्व्हेयर बेल्ट';
    if (currentLang === 'kn') return 'ಕನ್ವೇಯರ್ ಬೆಲ್ಟ್';
    return 'Conveyor Belt';
  }

  // 5. Hot Water Boiler
  if (lower.includes('boiler') || lower.includes('hot water')) {
    if (currentLang === 'mr') return 'इंडस्ट्रियल हॉट वॉटर बॉयलर';
    if (currentLang === 'kn') return 'ಕೈಗಾರಿಕಾ ಬಿಸಿನೀರಿನ ಬಾಯ್ಲರ್';
    return name;
  }

  // 6. Pomegranate / Anardana
  if (lower.includes('pomegranate') || lower.includes('anardana')) {
    if (currentLang === 'mr') return 'डाळिंब (अनारदाना) मशीन';
    if (currentLang === 'kn') return 'ದಾಳಿಂಬೆ (ಅನಾರ್ದಾನ) ಯಂತ್ರ';
    return name;
  }

  // 7. Chaff Cutter
  if (lower.includes('chaff') || lower.includes('cutter')) {
    if (currentLang === 'mr') return 'हेवी ड्युटी कडबा कुट्टी मशीन';
    if (currentLang === 'kn') return 'ಹೆವಿ ಡ್ಯೂಟಿ ಮೇವು ಕತ್ತರಿಸುವ ಯಂತ್ರ';
    return name;
  }

  // 8. Dry Date / Kharik
  if (lower.includes('date') || lower.includes('kharik')) {
    if (currentLang === 'mr') return 'खारिक कटिंग मशीन';
    if (currentLang === 'kn') return 'ಒಣ ಖರ್ಜೂರ ಕತ್ತರಿಸುವ ಯಂತ್ರ';
    return name;
  }

  // 9. Sizer / Grader / Grain
  if (lower.includes('sizer') || lower.includes('grader') || lower.includes('grain')) {
    if (currentLang === 'mr') return 'मल्टी-ग्रेन सायझर व ग्रेडर';
    if (currentLang === 'kn') return 'ಮಲ್ಟಿ-ಗ್ರೇನ್ ಸೈಜರ್ ಮತ್ತು ಗ್ರೇಡರ್';
    return name;
  }

  // 10. Turmeric / Polish
  if (lower.includes('turmeric') || lower.includes('polish')) {
    if (currentLang === 'mr') return 'हळद पॉलिश मशीन';
    if (currentLang === 'kn') return 'ಅರಿಶಿನ ಪಾಲಿಶ್ ಯಂತ್ರ';
    return name;
  }

  // 3. Check client-side cached translation
  try {
    const cacheKey = `prod_trans_${currentLang}_${encodeURIComponent(name.trim().slice(0, 100))}_${name.trim().length}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) return cached;
  } catch (e) {}

  return name;
};

// ─── LOCALIZED CATEGORIES ───────────────────────────────────────────────────
export const getLocalizedCategory = (category, t, lang = 'en', product = null) => {
  if (!category) return '';
  const currentLang = normalizeLang(lang);

  // 1. Direct language field on product object
  if (product && typeof product === 'object') {
    if (currentLang === 'mr' && (product.category_mr || product.categoryMr)) {
      return product.category_mr || product.categoryMr;
    }
    if (currentLang === 'kn' && (product.category_kn || product.categoryKn)) {
      return product.category_kn || product.categoryKn;
    }
  }

  const catLower = category.trim().toLowerCase();

  if (catLower === 'all') {
    return t ? t('catAll') : (currentLang === 'mr' ? 'सर्व' : currentLang === 'kn' ? 'ಎಲ್ಲಾ' : 'All');
  }
  if (catLower === 'food processing' || catLower.includes('food')) {
    return t ? t('catFoodProcessing') : (currentLang === 'mr' ? 'अन्न प्रक्रिया' : currentLang === 'kn' ? 'ಆಹಾರ ಸಂಸ್ಕರಣೆ' : 'Food Processing');
  }
  if (catLower.includes('material')) {
    return t ? t('catMaterialHandling') : (currentLang === 'mr' ? 'मटेरिअल हँडलिंग' : currentLang === 'kn' ? 'ಮೆಟೀರಿಯಲ್ ಹ್ಯಾಂಡ್ಲಿಂಗ್' : 'Material Handling');
  }
  if (catLower.includes('air')) {
    return t ? t('catAirHandling') : (currentLang === 'mr' ? 'एअर हँडलिंग' : currentLang === 'kn' ? 'ಏರ್ ಹ್ಯಾಂಡ್ಲಿಂಗ್' : 'Air Handling');
  }
  if (catLower.includes('heat') || catLower.includes('boiler')) {
    return t ? t('catHeatingSystems') : (currentLang === 'mr' ? 'हीटिंग सिस्टीम्स' : currentLang === 'kn' ? 'ಹೀಟಿಂಗ್ ಸಿಸ್ಟಮ್ಸ್' : 'Heating Systems');
  }
  if (catLower.includes('agri') || catLower.includes('automation')) {
    return t ? t('catAgri') : (currentLang === 'mr' ? 'कृषी यंत्रसामग्री' : currentLang === 'kn' ? 'ಕೃಷಿ ಯಂತ್ರೋಪಕರಣಗಳು' : 'Agricultural Automation');
  }
  if (catLower.includes('bedana') || catLower.includes('raisin')) {
    return currentLang === 'mr' ? 'बेदाणा प्रक्रिया' : currentLang === 'kn' ? 'ಒಣದ್ರಾಕ್ಷಿ ಸಂಸ್ಕರಣೆ' : 'Bedana Processing';
  }
  if (catLower.includes('washing') || catLower.includes('cleaning')) {
    return currentLang === 'mr' ? 'धुलाई व स्वच्छता' : currentLang === 'kn' ? 'ತೊಳೆಯುವುದು ಮತ್ತು ಸ್ವಚ್ಛಗೊಳಿಸುವಿಕೆ' : 'Washing & Cleaning';
  }
  if (catLower.includes('grading') || catLower.includes('sorting')) {
    return currentLang === 'mr' ? 'ग्रेडिंग व सॉर्टिंग' : currentLang === 'kn' ? 'ಗ್ರೇಡಿಂಗ್ ಮತ್ತು ವಿಂಗಡಣೆ' : 'Grading & Sorting';
  }
  if (catLower.includes('conveyor')) {
    return currentLang === 'mr' ? 'कन्व्हेयर व हँडलिंग' : currentLang === 'kn' ? 'ಕನ್ವೇಯರ್ ಮತ್ತು ಹ್ಯಾಂಡ್ಲಿಂಗ್' : 'Conveyor & Handling';
  }
  if (catLower.includes('industrial')) {
    return currentLang === 'mr' ? 'औद्योगिक उपकरणे' : currentLang === 'kn' ? 'ಕೈಗಾರಿಕಾ ಉಪಕರಣಗಳು' : 'Industrial Equipment';
  }
  if (catLower.includes('custom')) {
    return currentLang === 'mr' ? 'सानुकूल उपकरणे' : currentLang === 'kn' ? 'ಕಸ್ಟಮ್ ಉಪಕರಣಗಳು' : 'Custom Equipment';
  }

  // 3. Cached translation
  try {
    const cacheKey = `prod_trans_${currentLang}_${encodeURIComponent(category.trim().slice(0, 100))}_${category.trim().length}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) return cached;
  } catch (e) {}

  return t ? t(category, category) : category;
};

// ─── LOCALIZED CAPACITY ─────────────────────────────────────────────────────
export const getLocalizedCapacity = (capacity, lang = 'en', product = null) => {
  const currentLang = normalizeLang(lang);

  // 1. Direct language field on product object
  if (product && typeof product === 'object') {
    if (currentLang === 'mr' && (product.capacity_mr || product.capacityMr)) {
      return product.capacity_mr || product.capacityMr;
    }
    if (currentLang === 'kn' && (product.capacity_kn || product.capacityKn)) {
      return product.capacity_kn || product.capacityKn;
    }
  }

  const rawCap = capacity ? capacity.replace(/^capacity:\s*/i, '').trim() : '';
  if (!rawCap) return '';

  const prefix = currentLang === 'mr' ? 'क्षमता: ' : currentLang === 'kn' ? 'ಸಾಮರ್ಥ್ಯ: ' : 'Capacity: ';
  const lower = rawCap.toLowerCase();

  // As per requirements
  if (lower.includes('requirement') || lower.includes('per requirement') || lower.includes('as per')) {
    if (currentLang === 'mr') return `${prefix}गरजेनुसार सानुकूल`;
    if (currentLang === 'kn') return `${prefix}ಅಗತ್ಯಕ್ಕೆ ಅನುಗುಣವಾಗಿ`;
    return `${prefix}As Per Requirements`;
  }

  // 1 Ton, 2 Ton & 4 Ton per hour
  if (lower.includes('1 ton') && lower.includes('2 ton') && lower.includes('4 ton')) {
    if (currentLang === 'mr') return `${prefix}१, २ आणि ४ टन प्रति तास`;
    if (currentLang === 'kn') return `${prefix}೧, ೨ ಮತ್ತು ೪ ಟನ್ ಪ್ರತಿ ಗಂಟೆಗೆ`;
    return `${prefix}1, 2 & 4 TON/HR`;
  }

  // 2 To 4 Ton per hour
  if ((lower.includes('2 to 4') || lower.includes('2-4')) && lower.includes('ton')) {
    if (currentLang === 'mr') return `${prefix}२ ते ४ टन प्रति तास`;
    if (currentLang === 'kn') return `${prefix}೨ ರಿಂದ ೪ ಟನ್ ಪ್ರತಿ ಗಂಟೆಗೆ`;
    return `${prefix}2 to 4 TON/HR`;
  }

  // 500 KG & 1 Ton per hour
  if (lower.includes('500 kg') && lower.includes('1 ton')) {
    if (currentLang === 'mr') return `${prefix}५०० किलो आणि १ टन प्रति तास`;
    if (currentLang === 'kn') return `${prefix}೫೦೦ ಕೆಜಿ ಮತ್ತು ೧ ಟನ್ ಪ್ರತಿ ಗಂಟೆಗೆ`;
    return `${prefix}500 KG & 1 TON/HR`;
  }

  // 400 to 500 KG per hour
  if (lower.includes('400') && lower.includes('500')) {
    if (currentLang === 'mr') return `${prefix}४०० ते ५०० किलो प्रति तास`;
    if (currentLang === 'kn') return `${prefix}೪೦೦ ರಿಂದ ೫೦೦ ಕೆಜಿ ಪ್ರತಿ ಗಂಟೆಗೆ`;
    return `${prefix}400 to 500 KG/HR`;
  }

  // 30LTR, 50 LTR, 100 LTR, 300LTR, 500 LTR & 1000 LTR
  if (lower.includes('30') && lower.includes('1000')) {
    if (currentLang === 'mr') return `${prefix}३०, ५०, १००, ३००, ५०० आणि १००० लिटर`;
    if (currentLang === 'kn') return `${prefix}೩೦, ೫೦, ೧೦೦, ೩೦೦, ೫೦೦ ಮತ್ತು ೧೦೦೦ ಲೀಟರ್`;
    return `${prefix}30, 50, 100, 300, 500 & 1000 LTR`;
  }

  // 4 Ton per hour
  if (lower.includes('4 ton')) {
    if (currentLang === 'mr') return `${prefix}४ टन प्रति तास`;
    if (currentLang === 'kn') return `${prefix}೪ ಟನ್ ಪ್ರತಿ ಗಂಟೆಗೆ`;
    return `${prefix}4 TON PER HOUR`;
  }

  // 2 Ton per hour
  if (lower.includes('2 ton')) {
    if (currentLang === 'mr') return `${prefix}२ टन प्रति तास`;
    if (currentLang === 'kn') return `${prefix}೨ ಟನ್ ಪ್ರತಿ ಗಂಟೆಗೆ`;
    return `${prefix}2 TON PER HOUR`;
  }

  // 1 Ton per hour
  if (lower.includes('1 ton')) {
    if (currentLang === 'mr') return `${prefix}१ टन प्रति तास`;
    if (currentLang === 'kn') return `${prefix}೧ ಟನ್ ಪ್ರತಿ ಗಂಟೆಗೆ`;
    return `${prefix}1 TON PER HOUR`;
  }

  // 500 KG per hour
  if (lower.includes('500 kg')) {
    if (currentLang === 'mr') return `${prefix}५०० किलो प्रति तास`;
    if (currentLang === 'kn') return `${prefix}೫೦೦ ಕೆಜಿ ಪ್ರತಿ ಗಂಟೆಗೆ`;
    return `${prefix}500 KG PER HOUR`;
  }

  // 200 KG per hour
  if (lower.includes('200 kg') || lower.includes('200')) {
    if (currentLang === 'mr') return `${prefix}२०० किलो प्रति तास`;
    if (currentLang === 'kn') return `${prefix}೨೦೦ ಕೆಜಿ ಪ್ರತಿ ಗಂಟೆಗೆ`;
    return `${prefix}200 KG/HR`;
  }

  if (lower.includes('heavy duty')) {
    if (currentLang === 'mr') return `${prefix}हेवी ड्युटी औद्योगिक क्षमता`;
    if (currentLang === 'kn') return `${prefix}ಹೆವಿ ಡ್ಯೂಟಿ ಕೈಗಾರಿಕಾ ಸಾಮರ್ಥ್ಯ`;
    return `${prefix}Heavy Duty`;
  }

  // Try cached translation for dynamic capacity
  try {
    const cacheKey = `prod_trans_${currentLang}_${encodeURIComponent(rawCap.slice(0, 100))}_${rawCap.length}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) return `${prefix}${cached}`;
  } catch (e) {}

  return `${prefix}${rawCap}`;
};

// ─── LOCALIZED OVERVIEW / FULL DESCRIPTION ──────────────────────────────────
export const getLocalizedOverview = (product, t, lang = 'en') => {
  if (!product) return '';
  const currentLang = normalizeLang(lang);
  const displayName = getLocalizedProductName(product, lang);
  const lower = (product.name || '').toLowerCase();

  // 1. Direct language field on product object
  if (currentLang === 'mr') {
    if (product.fullDescription_mr || product.fullDescriptionMr) {
      return product.fullDescription_mr || product.fullDescriptionMr;
    }
    if (product.overview_mr || product.overviewMr) {
      return product.overview_mr || product.overviewMr;
    }
  }
  if (currentLang === 'kn') {
    if (product.fullDescription_kn || product.fullDescriptionKn) {
      return product.fullDescription_kn || product.fullDescriptionKn;
    }
    if (product.overview_kn || product.overviewKn) {
      return product.overview_kn || product.overviewKn;
    }
  }

  // If language is English
  if (currentLang === 'en') {
    if (product.fullDescription) return product.fullDescription;
    if (product.overview) return product.overview;
    if (lower.includes('bedana') || lower.includes('raisin')) {
      if (lower.includes('washing')) {
        return `Fully automated raisins washing line designed for deep hygienic cleaning, desurfacing, and dewatering with minimal water consumption and high efficiency.`;
      }
      return t ? t('pdBedanaOverview', { name: displayName }) : 'High performance raisin processing machine.';
    }
    if (lower.includes('classifier') || lower.includes('air classifier')) {
      return `High-efficiency industrial air classifier designed to separate dust, chaff, light impurities, and foreign particles from grains, seeds, and processed foods with aerodynamic precision.`;
    }
    if (lower.includes('elevator') || lower.includes('bucket elevator')) {
      return `Heavy-duty vertical bucket elevators engineered for smooth, continuous, and damage-free transportation of bulk grains, seeds, and food commodities. Features high-tensile food-grade belt and wear-resistant buckets.`;
    }
    if (lower.includes('conveyor') || lower.includes('loading')) {
      return `Mobile and adjustable loading conveyors engineered for heavy bags, crates, and bulk commodities. Dramatically reduces manual labor and loading turnaround times with reversible motor operation.`;
    }
    if (lower.includes('boiler')) {
      return `Durable, high-efficiency hot water boilers built with premium IS 2062 grade steel. Essential for large-scale agricultural and food processing applications. Features automatic temperature control and advanced safety systems.`;
    }
    if (lower.includes('pomegranate') || lower.includes('anardana')) {
      return `Advanced pomegranate processing machinery for extracting anardana with maximum yield and minimal waste. Features automated sorting and a specialized drum design to prevent seed crushing.`;
    }
    if (lower.includes('chaff')) {
      return `Our legacy product. Extremely durable chaff cutters designed for large dairy farms. Features self-sharpening blades, zero maintenance gearbox, and adjustable cut sizes for different types of fodder.`;
    }
    if (lower.includes('date') || lower.includes('kharik')) {
      return `Precision cutting machines for dry dates with adjustable thickness settings. Ensures uniform cutting with minimal powder generation. Extremely easy to clean and operate.`;
    }
    if (lower.includes('sizer') || lower.includes('grader')) {
      return `Advanced vibratory grain and fruit grading systems with unmatched accuracy. Comes with interchangeable screens for different crops. Ideal for wheat, soybean, jowar, and pomegranates.`;
    }
    if (lower.includes('turmeric')) {
      return `High-speed turmeric polishing drum that gives a brilliant yellow finish to raw turmeric without chemical additives. Built with thick mild steel for decades of wear-free usage.`;
    }
    return product.overview || product.shortDescription || (t ? t('pdGeneralOverview', { name: displayName }) : '');
  }

  // Marathi translation
  if (currentLang === 'mr') {
    if (lower.includes('bedana') || lower.includes('raisin')) {
      if (lower.includes('washing')) {
        return `बेदाणा आणि मनुका यांच्या सखोल स्वच्छतेसाठी पूर्णपणे स्वयंचलित वॉशिंग लाईन प्रणाली. कमी पाण्याच्या वापरात जास्तीत जास्त स्वच्छता, दर्जा आणि उच्च उत्पादन क्षमता सुनिश्चित करते.`;
      }
      return t ? t('pdBedanaOverview', { name: displayName }) : 'उच्च कार्यक्षमतेचे बेदाणा प्रक्रिया मशीन.';
    }
    if (lower.includes('classifier') || lower.includes('air classifier')) {
      return `अचूक हवेच्या प्रवाहाचा वापर करून धान्य, बियाणे आणि प्रक्रिया केलेल्या अन्नधान्यामधून धूळ, कचरा आणि हलके कण वेगळे करणारे उच्च कार्यक्षमतेचे एअर क्लासिफायर मशीन.`;
    }
    if (lower.includes('elevator') || lower.includes('bucket elevator')) {
      return `धान्य, बियाणे आणि प्रक्रिया केलेले अन्नपदार्थ सुरक्षितपणे आणि उभ्या दिशेने वाहून नेण्यासाठी हेवी-ड्युटी बकेट एलिव्हेटर. यामध्ये उच्च दर्जाचा बेल्ट आणि टिकाऊ बकेट्स आहेत.`;
    }
    if (lower.includes('conveyor') || lower.includes('loading')) {
      return `मोबाईल आणि समायोज्य ट्रक लोडिंग कन्व्हेयर. हे जड पोती आणि क्रेट्ससाठी मानवी श्रम आणि लोडिंगचा वेळ लक्षणीयरीत्या कमी करते. लोडिंग आणि अनलोडिंग दोन्हीसाठी रिव्हर्सिबल मोटरसह सुसज्ज.`;
    }
    if (lower.includes('boiler')) {
      return `प्रिमियम IS 2062 ग्रेड स्टीलने बनवलेले टिकाऊ, उच्च कार्यक्षमतेचे हॉट वॉटर बॉयलर. मोठ्या प्रमाणावर कृषी व अन्न प्रक्रिया उद्योगांसाठी अत्यंत उपयुक्त. स्वयंचलित तापमान नियंत्रण आणि प्रगत सुरक्षा प्रणालीसह सुसज्ज.`;
    }
    if (lower.includes('pomegranate') || lower.includes('anardana')) {
      return `कमीत कमी नासाडीसह जास्तीत जास्त अनारदाना काढण्यासाठी प्रगत डाळिंब प्रक्रिया मशिनरी. यामध्ये बियांचा चुरा न होता दाणे वेगळे करण्यासाठी स्वयंचलित सॉर्टिंग आणि विशेष ड्रम डिझाइन आहे.`;
    }
    if (lower.includes('chaff')) {
      return `मोठ्या डेअरी फार्मसाठी डिझाइन केलेले अत्यंत टिकाऊ कडबा कुट्टी मशीन. यात सेल्फ-शार्पनिंग ब्लेड, शून्य देखभाल गिअरबॉक्स आणि विविध प्रकारच्या चाऱ्यासाठी समायोज्य कट साइज उपलब्ध आहेत.`;
    }
    if (lower.includes('date') || lower.includes('kharik')) {
      return `अॅडजस्टेबल जाडी सेटिंग्जसह सुक्या खारीकसाठी अचूक कटिंग मशीन. कमीत कमी पावडर निर्मितीसह एकसमान कटिंग सुनिश्चित करते. वापरण्यास आणि स्वच्छ करण्यास अत्यंत सोपे.`;
    }
    if (lower.includes('sizer') || lower.includes('grader')) {
      return `अचूकतेसह प्रगत व्हायब्रेटरी धान्य आणि फळे प्रतवारी (ग्रेडिंग) प्रणाली. विविध पिकांसाठी बदलण्यायोग्य जाळ्यांसह उपलब्ध. गहू, सोयाबीन, ज्वारी आणि डाळिंबासाठी आदर्श.`;
    }
    if (lower.includes('turmeric')) {
      return `रासायनिक घटकांशिवाय कच्च्या हळदीला चमकदार पिवळा रंग देणारा हाय-स्पीड हळद पॉलिशिंग ड्रम. अनेक दशके न झिजता चालण्यासाठी जाड माइल्ड स्टीलने बनवलेले.`;
    }

    // Try cached translation for dynamic product
    try {
      const baseText = product.fullDescription || product.overview || product.shortDescription || '';
      if (baseText) {
        const cacheKey = `prod_trans_mr_${encodeURIComponent(baseText.slice(0, 100))}_${baseText.length}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached) return cached;
      }
    } catch (e) {}

    return t ? t('pdGeneralOverview', { name: displayName }) : 'उच्च दर्जाचे औद्योगिक प्रक्रिया मशीन.';
  }

  // Kannada translation
  if (currentLang === 'kn') {
    if (lower.includes('bedana') || lower.includes('raisin')) {
      if (lower.includes('washing')) {
        return `ಒಣದ್ರಾಕ್ಷಿ ಮತ್ತು ಆಹಾರ ಧಾನ್ಯಗಳ ಆಳವಾದ ಸ್ವಚ್ಛತೆಗಾಗಿ ಸಂಪೂರ್ಣ ಸ್ವಯಂಚಾಲಿತ ವಾಷಿಂಗ್ ಲೈನ್. ಕನಿಷ್ಠ ನೀರಿನ ಬಳಕೆಯೊಂದಿಗೆ ಗರಿಷ್ಠ ನೈರ್ಮಲ್ಯ ಮತ್ತು ದಕ್ಷತೆ ನೀಡುತ್ತದೆ.`;
      }
      return t ? t('pdBedanaOverview', { name: displayName }) : 'ಹೆಚ್ಚಿನ ಕಾರ್ಯಕ್ಷಮತೆಯ ಒಣದ್ರಾಕ್ಷಿ ಸಂಸ್ಕರಣಾ ಯಂತ್ರ.';
    }
    if (lower.includes('classifier') || lower.includes('air classifier')) {
      return `ನಿಖರವಾದ ಗಾಳಿಯ ಪ್ರವಾಹವನ್ನು ಬಳಸಿಕೊಂಡು ಧಾನ್ಯಗಳು, ಬೀಜಗಳು ಮತ್ತು ಸಂಸ್ಕರಿಸಿದ ಆಹಾರ ಪದಾರ್ಥಗಳಿಂದ ಧೂಳು, ಕಸ ಮತ್ತು ಹಗುರವಾದ ಕಣಗಳನ್ನು ಬೇರ್ಪಡಿಸುವ ಉನ್ನತ ದಕ್ಷತೆಯ ಏರ್ ಕ್ಲಾಸಿಫೈಯರ್ ಯಂತ್ರ.`;
    }
    if (lower.includes('elevator') || lower.includes('bucket elevator')) {
      return `ಧಾನ್ಯಗಳು, ಬೀಜಗಳು ಮತ್ತು ಆಹಾರ ಪದಾರ್ಥಗಳನ್ನು ಲಂಬವಾಗಿ ಸುರಕ್ಷಿತವಾಗಿ ಸಾಗಿಸಲು ಹೆವಿ-ಡ್ಯೂಟಿ ಬಕೆಟ್ ಎಲಿವೇಟರ್. ಉತ್ತಮ ಗುಣಮಟ್ಟದ ಬೆಲ್ಟ್ ಮತ್ತು ಬಾಳಿಕೆ ಬರುವ ಬಕೆಟ್‌ಗಳನ್ನು ಹೊಂದಿದೆ.`;
    }
    if (lower.includes('conveyor') || lower.includes('loading')) {
      return `ಮೊಬೈಲ್ ಮತ್ತು ಹೊಂದಾಣಿಕೆ ಮಾಡಬಹುದಾದ ಟ್ರಕ್ ಲೋಡಿಂಗ್ ಕನ್ವೇಯರ್. ಇದು ಭಾರವಾದ ಚೀಲಗಳು ಮತ್ತು ಕ್ರೇಟ್‌ಗಳ ಲೋಡಿಂಗ್ ಸಮಯ ಮತ್ತು ದೈಹಿಕ ಶ್ರಮವನ್ನು ಗಣನೀಯವಾಗಿ ಕಡಿಮೆ ಮಾಡುತ್ತದೆ.`;
    }
    if (lower.includes('boiler')) {
      return `ಪ್ರೀಮಿಯಂ IS 2062 ಗ್ರೇಡ್ ಸ್ಟೀಲ್‌ನಿಂದ ನಿರ್ಮಿಸಲಾದ ಬಾಳಿಕೆ ಬರುವ, ಹೆಚ್ಚಿನ ದಕ್ಷತೆಯ ಬಿಸಿನೀರಿನ ಬಾಯ್ಲರ್‌ಗಳು. ಬೃಹತ್ ಪ್ರಮಾಣದ ಕೃಷಿ ಮತ್ತು ಆಹಾರ ಸಂಸ್ಕರಣೆಗೆ ಅತ್ಯಗತ್ಯ.`;
    }
    if (lower.includes('pomegranate') || lower.includes('anardana')) {
      return `ಕನಿಷ್ಠ ತ್ಯಾಜ್ಯದೊಂದಿಗೆ ಗರಿಷ್ಠ ಇಳುವರಿಯ ಅನಾರ್‌ದಾನ ಹೊರತೆಗೆಯಲು ಸುಧಾರಿತ ದಾಳಿಂಬೆ ಸಂಸ್ಕರಣಾ ಯಂತ್ರೋಪಕರಣ.`;
    }
    if (lower.includes('chaff')) {
      return `ದೊಡ್ಡ ಡೈರಿ ಫಾರ್ಮ್‌ಗಳಿಗಾಗಿ ವಿನ್ಯಾಸಗೊಳಿಸಲಾದ ಅತ್ಯಂತ ಬಾಳಿಕೆ ಬರುವ ಮೇವು ಕತ್ತರಿಸುವ ಯಂತ್ರ. ಸೆಲ್ಫ್-ಶಾರ್ಪನಿಂಗ್ ಬ್ಲೇಡ್‌ಗಳು ಮತ್ತು ಗೇರ್‌ಬಾಕ್ಸ್ ಒಳಗೊಂಡಿದೆ.`;
    }
    if (lower.includes('date') || lower.includes('kharik')) {
      return `ಹೊಂದಾಣಿಕೆ ಮಾಡಬಹುದಾದ ದಪ್ಪದ ಸೆಟ್ಟಿಂಗ್‌ಗಳೊಂದಿಗೆ ಒಣ ಖರ್ಜೂರಕ್ಕಾಗಿ ನಿಖರವಾದ ಕತ್ತರಿಸುವ ಯಂತ್ರ. ಕನಿಷ್ಠ ಪುಡಿ ಉತ್ಪಾದನೆಯೊಂದಿಗೆ ಏಕರೂಪದ ಕಟಿಂಗ್ ನೀಡುತ್ತದೆ.`;
    }
    if (lower.includes('sizer') || lower.includes('grader')) {
      return `ಸಾಟಿಯಿಲ್ಲದ ನಿಖರತೆಯೊಂದಿಗೆ ಸುಧಾರಿತ ವೈಬ್ರೇಟರಿ ಧಾನ್ಯ ಮತ್ತು ಹಣ್ಣುಗಳ ಗ್ರೇಡಿಂಗ್ ವ್ಯವಸ್ಥೆ.`;
    }
    if (lower.includes('turmeric')) {
      return `ರಾಸಾಯನಿಕ ಮುಕ್ತವಾಗಿ ಹಸಿ ಅರಿಶಿನಕ್ಕೆ ಹೊಳೆಯುವ ಹಳದಿ ಹೊಳಪನ್ನು ನೀಡುವ ಹೈ-ಸ್ಪೀಡ್ ಅರಿಶಿನ ಪಾಲಿಶ್ ಡ್ರಮ್.`;
    }

    // Try cached translation for dynamic product
    try {
      const baseText = product.fullDescription || product.overview || product.shortDescription || '';
      if (baseText) {
        const cacheKey = `prod_trans_kn_${encodeURIComponent(baseText.slice(0, 100))}_${baseText.length}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached) return cached;
      }
    } catch (e) {}

    return t ? t('pdGeneralOverview', { name: displayName }) : 'ಉನ್ನತ ಗುಣಮಟ್ಟದ ಕೈಗಾರಿಕಾ ಸಂಸ್ಕರಣಾ ಯಂತ್ರೋಪಕರಣ.';
  }

  return product.fullDescription || product.overview || product.shortDescription || '';
};

// ─── LOCALIZED SHORT DESCRIPTION (CARDS) ───────────────────────────────────
export const getLocalizedShortDesc = (product, t, lang = 'en') => {
  if (!product) return '';
  const currentLang = normalizeLang(lang);

  // 1. Direct language field on product object
  if (currentLang === 'mr') {
    if (product.shortDescription_mr || product.shortDescriptionMr) {
      return product.shortDescription_mr || product.shortDescriptionMr;
    }
    if (product.description_mr || product.descriptionMr) {
      return product.description_mr || product.descriptionMr;
    }
  }
  if (currentLang === 'kn') {
    if (product.shortDescription_kn || product.shortDescriptionKn) {
      return product.shortDescription_kn || product.shortDescriptionKn;
    }
    if (product.description_kn || product.descriptionKn) {
      return product.description_kn || product.descriptionKn;
    }
  }

  const lower = (product.name || '').toLowerCase();

  if (lower.includes('bedana') || lower.includes('raisin')) {
    if (lower.includes('washing')) {
      if (currentLang === 'mr') return 'बेदाणा आणि मनुका स्वच्छ धुण्यासाठी संपूर्ण स्वयंचलित वॉशिंग लाईन प्रणाली.';
      if (currentLang === 'kn') return 'ಒಣದ್ರಾಕ್ಷಿ ಸ್ವಚ್ಛಗೊಳಿಸಲು ಸಂಪೂರ್ಣ ಸ್ವಯಂಚಾಲಿತ ವಾಷಿಂಗ್ ಲೈನ್ ವ್ಯವಸ್ಥೆ.';
      return 'Fully automated washing line system for deep hygienic cleaning and processing.';
    }
    return t ? t('pdBedanaShortDesc') : 'High capacity raisin processing machine.';
  }

  if (currentLang === 'en') {
    if (lower.includes('classifier') || lower.includes('air classifier')) return 'High-efficiency air classifier to separate dust and impurities with aerodynamic precision.';
    if (lower.includes('elevator') || lower.includes('bucket elevator')) return 'Heavy-duty vertical bucket elevator engineered for damage-free bulk material transport.';
    if (lower.includes('conveyor') || lower.includes('loading')) return 'Durable and adjustable conveyor system for seamless material movement.';
    return product.shortDescription || product.description || '';
  }

  if (currentLang === 'mr') {
    if (lower.includes('classifier') || lower.includes('air classifier')) return 'अचूक हवेच्या प्रवाहाने धान्य आणि बियाण्यांमधील कचरा वेगळा करणारे प्रगत एअर क्लासिफायर.';
    if (lower.includes('elevator') || lower.includes('bucket elevator')) return 'धान्य, बियाणे आणि उत्पादने उभ्या दिशेने वाहून नेण्यासाठी हेवी-ड्युटी बकेट एलिव्हेटर.';
    if (lower.includes('conveyor') || lower.includes('loading')) return 'जड पोती आणि उत्पादनांच्या सुरळीत वाहतुकीसाठी टिकाऊ व सानुकूल कन्व्हेयर प्रणाली.';
    if (lower.includes('boiler')) return 'मोठ्या प्रमाणावर कृषी व अन्न प्रक्रिया उद्योगांसाठी उच्च कार्यक्षमतेचे हॉट वॉटर बॉयलर.';
    if (lower.includes('pomegranate') || lower.includes('anardana')) return 'कमीत कमी नासाडीसह जास्तीत जास्त अनारदाना काढण्यासाठी प्रगत डाळिंब प्रक्रिया मशीन.';
    if (lower.includes('chaff')) return 'मोठ्या डेअरी फार्मसाठी टिकाऊ आणि स्वयंचलित कडबा कुट्टी मशीन.';
    if (lower.includes('date') || lower.includes('kharik')) return 'सुक्या खारीकच्या अचूक आणि एकसमान कटिंगसाठी प्रगत कटिंग मशीन.';
    if (lower.includes('sizer') || lower.includes('grader')) return 'विविध धान्ये आणि फळांच्या अचूक प्रतवारीसाठी हाय-स्पीड व्हायब्रेटरी ग्रेडर.';
    if (lower.includes('turmeric')) return 'कच्च्या हळदीला चमकदार पिवळा रंग देणारा हाय-स्पीड पॉलिशिंग ड्रम.';

    // Try cached translation for dynamic product
    try {
      const baseText = product.shortDescription || product.description || '';
      if (baseText) {
        const cacheKey = `prod_trans_mr_${encodeURIComponent(baseText.slice(0, 100))}_${baseText.length}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached) return cached;
      }
    } catch (e) {}

    return 'उच्च गुणवत्ता आणि टिकाऊ बांधकाम असलेले औद्योगिक प्रक्रिया मशीन.';
  }

  if (currentLang === 'kn') {
    if (lower.includes('classifier') || lower.includes('air classifier')) return 'ಗಾಳಿಯ ಪ್ರವಾಹದಿಂದ ಧಾನ್ಯಗಳು ಮತ್ತು ಬೀಜಗಳಿಂದ ಕಸವನ್ನು ಬೇರ್ಪಡಿಸುವ ಸುಧಾರಿತ ಏರ್ ಕ್ಲಾಸಿಫೈಯರ್.';
    if (lower.includes('elevator') || lower.includes('bucket elevator')) return 'ಧಾನ್ಯಗಳು ಮತ್ತು ಉತ್ಪನ್ನಗಳನ್ನು ಲಂಬವಾಗಿ ಸುಲಭವಾಗಿ ಸಾಗಿಸಲು ಬಾಳಿಕೆ ಬರುವ ಬಕೆಟ್ ಎಲಿವೇಟರ್.';
    if (lower.includes('conveyor') || lower.includes('loading')) return 'ಭಾರವಾದ ಚೀಲಗಳು ಮತ್ತು ಉತ್ಪನ್ನಗಳ ಸಾಗಣೆಗಾಗಿ ಬಾಳಿಕೆ ಬರುವ ಕನ್ವೇಯರ್ ವ್ಯವಸ್ಥೆ.';
    if (lower.includes('boiler')) return 'ಕೃಷಿ ಮತ್ತು ಆಹಾರ ಸಂಸ್ಕರಣಾ ಕೈಗಾರಿಕೆಗಳಿಗೆ ಹೆಚ್ಚಿನ ದಕ್ಷತೆಯ ಬಿಸಿನೀರಿನ ಬಾಯ್ಲರ್.';
    if (lower.includes('pomegranate') || lower.includes('anardana')) return 'ಗರಿಷ್ಠ ಇಳುವರಿಯ ಅನಾರ್‌ದಾನ ಹೊರತೆಗೆಯಲು ಸುಧಾರಿತ ದಾಳಿಂಬೆ ಸಂಸ್ಕರಣಾ ಯಂತ್ರ.';
    if (lower.includes('chaff')) return 'ದೊಡ್ಡ ಡೈರಿ ಫಾರ್ಮ್‌ಗಳಿಗಾಗಿ ಬಾಳಿಕೆ ಬರುವ ಮೇವು ಕತ್ತರಿಸುವ ಯಂತ್ರ.';
    if (lower.includes('date') || lower.includes('kharik')) return 'ಒಣ ಖರ್ಜೂರದ ಏಕರೂಪದ ಕಟಿಂಗ್‌ಗಾಗಿ ನಿಖರವಾದ ಕತ್ತರಿಸುವ ಯಂತ್ರ.';
    if (lower.includes('sizer') || lower.includes('grader')) return 'ಧಾನ್ಯಗಳು ಮತ್ತು ಹಣ್ಣುಗಳ ನಿಖರವಾದ ವಿಂಗಡಣೆಗಾಗಿ ಸುಧಾರಿತ ಗ್ರೇಡರ್.';
    if (lower.includes('turmeric')) return 'ಹಸಿ ಅರಿಶಿನಕ್ಕೆ ಹೊಳೆಯುವ ಹೊಳಪನ್ನು ನೀಡುವ ಹೈ-ಸ್ಪೀಡ್ ಪಾಲಿಶ್ ಡ್ರಮ್.';

    // Try cached translation for dynamic product
    try {
      const baseText = product.shortDescription || product.description || '';
      if (baseText) {
        const cacheKey = `prod_trans_kn_${encodeURIComponent(baseText.slice(0, 100))}_${baseText.length}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached) return cached;
      }
    } catch (e) {}

    return 'ಹೆಚ್ಚಿನ ಗುಣಮಟ್ಟ ಮತ್ತು ದೀರ್ಘಾವಧಿ ಬಾಳಿಕೆಯ ಕೈಗಾರಿಕಾ ಸಂಸ್ಕರಣಾ ಯಂತ್ರೋಪಕರಣ.';
  }

  return product.shortDescription || product.description || '';
};

// ─── LOCALIZED DETAILED SPECS ───────────────────────────────────────────────
export const getLocalizedDetailedSpecs = (product, t, lang = 'en') => {
  if (!product) return [];
  const currentLang = normalizeLang(lang);
  const displayName = getLocalizedProductName(product, lang);

  // 1. Direct language field on product object
  if (currentLang === 'mr') {
    if (product.detailedSpecs_mr && Array.isArray(product.detailedSpecs_mr) && product.detailedSpecs_mr.length > 0) {
      return product.detailedSpecs_mr;
    }
  }
  if (currentLang === 'kn') {
    if (product.detailedSpecs_kn && Array.isArray(product.detailedSpecs_kn) && product.detailedSpecs_kn.length > 0) {
      return product.detailedSpecs_kn;
    }
  }

  const lower = (product.name || '').toLowerCase();
  const isBedana = lower.includes('bedana') || lower.includes('raisin');

  if (isBedana) {
    return [
      t ? t('pdBedanaSpec1', { name: displayName }) : `Designed specifically for raisin processing with maximum output.`,
      t ? t('pdBedanaSpec2') : `Heavy-duty stainless steel contact parts for food hygiene.`,
      t ? t('pdBedanaSpec3') : `Adjustable speeds and gentle handling to prevent damage.`,
      t ? t('pdBedanaSpec4') : `Integrated dust suction and sorting mechanisms.`
    ];
  }

  // If product has fullDescription with paragraphs, use them
  const fullDesc = currentLang === 'mr' ? (product.fullDescription_mr || product.fullDescription)
                 : currentLang === 'kn' ? (product.fullDescription_kn || product.fullDescription)
                 : product.fullDescription;

  if (fullDesc && fullDesc.includes('\n')) {
    const paragraphs = fullDesc.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
    if (paragraphs.length > 1) return paragraphs;
  }

  return [
    t ? t('pdGeneralSpec1', { name: displayName }) : `Engineered with high tensile materials for commercial industrial operation.`,
    t ? t('pdGeneralSpec2') : `Designed for energy efficiency, minimal maintenance, and continuous performance.`,
    t ? t('pdGeneralSpec3') : `Complies with food safety and industrial manufacturing standards.`
  ];
};
